import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useEffect } from 'react'
import { format, parseISO } from 'date-fns'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'

// ── Fetch tasks with due_date in range ───────────────────────────────────────
const useDueTasks = (rangeStart, rangeEnd, enabled) => {
  const { household } = useAuth()
  return useQuery({
    queryKey: ['system-tasks', household?.id, rangeStart, rangeEnd],
    enabled:  enabled && !!household?.id,
    staleTime: 0,
    refetchInterval: 60_000,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('id, title, due_date, assigned_to, completed_at')
        .eq('household_id', household.id)
        .not('due_date', 'is', null)
        .gte('due_date', rangeStart)
        .lte('due_date', rangeEnd)
      if (error) throw error
      return data ?? []
    },
  })
}

// ── Fetch meal plan slots in range ───────────────────────────────────────────
const useMealSlots = (rangeStart, rangeEnd, enabled) => {
  const { household } = useAuth()
  return useQuery({
    queryKey: ['system-meals', household?.id, rangeStart, rangeEnd],
    enabled:  enabled && !!household?.id,
    staleTime: 0,
    refetchInterval: 60_000,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('meal_plan_slots')
        .select('id, date, slot_name, custom_name, recipes(title)')
        .eq('household_id', household.id)
        .gte('date', rangeStart.slice(0, 10))
        .lte('date', rangeEnd.slice(0, 10))
      if (error) throw error
      return data ?? []
    },
  })
}

// ── Fetch birthdays and compute upcoming occurrences in range ────────────────
const useBirthdaySlots = (rangeStart, rangeEnd, enabled) => {
  const { household } = useAuth()
  return useQuery({
    queryKey: ['system-birthdays', household?.id],
    enabled:  enabled && !!household?.id,
    staleTime: 0,
    refetchInterval: 60_000,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('birthdays')
        .select('id, name, birth_date')
        .eq('household_id', household.id)
      if (error) throw error
      return data ?? []
    },
  })
}

// ── Fetch bills for the household ────────────────────────────────────────────
const useBillSlots = (enabled) => {
  const { household } = useAuth()
  return useQuery({
    queryKey: ['system-bills', household?.id],
    enabled:  enabled && !!household?.id,
    staleTime: 0,
    refetchInterval: 60_000,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('bills')
        .select('id, name, due_day, frequency')
        .eq('household_id', household.id)
      if (error) throw error
      return data ?? []
    },
  })
}

const nextOccurrenceInRange = (birthDateStr, start, end) => {
  const birth = parseISO(birthDateStr)
  const startDate = new Date(start)
  const endDate   = new Date(end)
  // Try years from rangeStart year -1 to +1 to find one that lands in range
  for (let yearOffset = -1; yearOffset <= 1; yearOffset++) {
    const d = new Date(startDate.getFullYear() + yearOffset, birth.getMonth(), birth.getDate())
    if (d >= startDate && d <= endDate) return d
  }
  return null
}

// ── Realtime invalidation for system tables ───────────────────────────────────
const useSystemRealtime = (householdId) => {
  const qc = useQueryClient()
  useEffect(() => {
    if (!householdId) return
    const ch = supabase
      .channel(`system-cal-${householdId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks',           filter: `household_id=eq.${householdId}` }, () => qc.invalidateQueries({ queryKey: ['system-tasks',     householdId] }))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'meal_plan_slots',  filter: `household_id=eq.${householdId}` }, () => qc.invalidateQueries({ queryKey: ['system-meals',     householdId] }))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'birthdays',        filter: `household_id=eq.${householdId}` }, () => qc.invalidateQueries({ queryKey: ['system-birthdays', householdId] }))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bills',            filter: `household_id=eq.${householdId}` }, () => qc.invalidateQueries({ queryKey: ['system-bills',     householdId] }))
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [householdId, qc])
}

// ── Combined hook ─────────────────────────────────────────────────────────────
export const useSystemCalendarEvents = (rangeStart, rangeEnd, hiddenIds) => {
  const { household } = useAuth()
  useSystemRealtime(household?.id)

  const showTasks     = !hiddenIds.has('system:tasks')
  const showMeals     = !hiddenIds.has('system:meals')
  const showBirthdays = !hiddenIds.has('system:birthdays')
  const showBills     = !hiddenIds.has('system:bills')

  const { data: tasks     = [] } = useDueTasks(rangeStart, rangeEnd, showTasks)
  const { data: mealSlots = [] } = useMealSlots(rangeStart, rangeEnd, showMeals)
  const { data: birthdays = [] } = useBirthdaySlots(rangeStart, rangeEnd, showBirthdays)
  const { data: bills     = [] } = useBillSlots(showBills)

  return useMemo(() => {
    const events = []

    if (showTasks) {
      for (const t of tasks) {
        if (t.completed_at) continue
        events.push({
          _system: true,
          _key: `task:${t.id}`,
          id:   `task:${t.id}`,
          title:       t.title,
          start_time:  t.due_date,
          end_time:    null,
          is_all_day:  true,
          calendar_id: 'system:tasks',
          calendars:   { id: 'system:tasks', name: 'Tasks', color: '#10b981' },
          event_members: t.assigned_to ? [{ user_id: t.assigned_to }] : [],
          recurrence_rule: null,
        })
      }
    }

    if (showMeals) {
      for (const s of mealSlots) {
        const name = s.recipes?.title ?? s.custom_name
        if (!name) continue
        const label = s.slot_name.charAt(0).toUpperCase() + s.slot_name.slice(1)
        events.push({
          _system: true,
          _key: `meal:${s.id}`,
          id:   `meal:${s.id}`,
          title:       `${label}: ${name}`,
          start_time:  s.date,
          end_time:    null,
          is_all_day:  true,
          calendar_id: 'system:meals',
          calendars:   { id: 'system:meals', name: 'Meals', color: '#f97316' },
          event_members: [],
          recurrence_rule: null,
        })
      }
    }

    if (showBirthdays) {
      for (const b of birthdays) {
        const d = nextOccurrenceInRange(b.birth_date, rangeStart, rangeEnd)
        if (!d) continue
        events.push({
          _system: true,
          _key: `bday:${b.id}`,
          id:   `bday:${b.id}`,
          title:       `🎂 ${b.name}'s Birthday`,
          start_time:  format(d, 'yyyy-MM-dd'),
          end_time:    null,
          is_all_day:  true,
          calendar_id: 'system:birthdays',
          calendars:   { id: 'system:birthdays', name: 'Birthdays', color: '#ec4899' },
          event_members: [],
          recurrence_rule: null,
        })
      }
    }

    if (showBills) {
      const rangeStartDate = new Date(rangeStart)
      const rangeEndDate   = new Date(rangeEnd)
      for (const bill of bills) {
        const cur = new Date(rangeStartDate.getFullYear(), rangeStartDate.getMonth(), 1)
        while (cur <= rangeEndDate) {
          const daysInMonth = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate()
          const day = Math.min(bill.due_day, daysInMonth)
          const dueDate = new Date(cur.getFullYear(), cur.getMonth(), day)
          if (dueDate >= rangeStartDate && dueDate <= rangeEndDate) {
            const dateStr = format(dueDate, 'yyyy-MM-dd')
            events.push({
              _system: true,
              _key:   `bill:${bill.id}:${dateStr}`,
              id:     `bill:${bill.id}:${dateStr}`,
              title:       bill.name,
              start_time:  dateStr,
              end_time:    null,
              is_all_day:  true,
              calendar_id: 'system:bills',
              calendars:   { id: 'system:bills', name: 'Bills', color: '#3b82f6' },
              event_members: [],
              recurrence_rule: null,
            })
          }
          cur.setMonth(cur.getMonth() + 1)
        }
      }
    }

    return events
  }, [tasks, mealSlots, birthdays, bills, showTasks, showMeals, showBirthdays, showBills, rangeStart, rangeEnd])
}
